// ============================================================================
// S16 Ravo Connect — Shopify integration client-side helpers (pure functions).
//
// These helpers enforce the Gate-2b / Production-Connection-Contract invariants
// on the dashboard side:
//   * canonical *.myshopify.com domain entry + validation;
//   * credentials/tokens NEVER enter Vue state, storage or any response we keep;
//   * a provider Location GID never lands in a UUID (ravo_site_id / catalogue
//     scope) field;
//   * durable lifecycle + phase state machines mirrored from the backend so the
//     UI can render/advance without guessing.
//
// Nothing here performs I/O — it is unit-test friendly and imported by the
// store, service sanitizer and components.
// ============================================================================

// ---------------------------------------------------------------------------
// Domain canonicalization / validation
// ---------------------------------------------------------------------------

// Shopify shop sub-domain: lowercase alphanumerics and single hyphens, 1..60
// chars, not starting/ending with a hyphen. The canonical host is always
// "{shop}.myshopify.com".
const SHOP_SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/;
const MYSHOPIFY_SUFFIX = ".myshopify.com";

/**
 * Reduce arbitrary operator input to a bare shop sub-domain.
 * Accepts: "acme", "acme.myshopify.com", "https://acme.myshopify.com/admin",
 * "ACME.MyShopify.com ", "acme.myshopify.com/". Returns "" when nothing usable.
 */
export function extractShopSubdomain(raw) {
  if (raw == null) return "";
  let s = String(raw).trim().toLowerCase();
  if (!s) return "";
  // strip scheme
  s = s.replace(/^https?:\/\//, "");
  // strip any path / query / fragment
  s = s.split("/")[0].split("?")[0].split("#")[0];
  // strip trailing dots
  s = s.replace(/\.+$/, "");
  if (!s) return "";
  // strip the myshopify suffix if present
  if (s.endsWith(MYSHOPIFY_SUFFIX)) {
    s = s.slice(0, -MYSHOPIFY_SUFFIX.length);
  } else if (s.includes(".")) {
    // A non-myshopify custom domain (e.g. "shop.acme.com") is NOT canonical.
    // We only accept the sub-domain form; take the first label but flag it as
    // invalid via canonicalShopDomain() below.
    s = s.split(".")[0];
  }
  return s;
}

/** True when `raw` resolves to a valid canonical *.myshopify.com shop. */
export function isValidShopDomain(raw) {
  const sub = extractShopSubdomain(raw);
  if (!sub) return false;
  // Reject anything that looked like a non-myshopify custom domain.
  const lowered = String(raw).trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0];
  if (lowered.includes(".") && !lowered.endsWith(MYSHOPIFY_SUFFIX)) return false;
  return SHOP_SUBDOMAIN_RE.test(sub);
}

/** Canonical "{shop}.myshopify.com" or null when invalid. */
export function canonicalShopDomain(raw) {
  if (!isValidShopDomain(raw)) return null;
  return `${extractShopSubdomain(raw)}${MYSHOPIFY_SUFFIX}`;
}

// ---------------------------------------------------------------------------
// Credential safety
// ---------------------------------------------------------------------------

// Any key that could carry a secret/token. If the backend ever regresses and
// leaks one of these, the sanitizer drops it before it can reach Vue state,
// localStorage, sessionStorage or a persisted store.
const CREDENTIAL_KEY_RE =
  /(access[_-]?token|refresh[_-]?token|token_bundle|client[_-]?secret|api[_-]?secret|webhook[_-]?secret|app[_-]?secret|(^|_)secret($|_)|password|ciphertext|bearer|authorization|private[_-]?key)/i;

// `token` alone is ambiguous (e.g. install "state" tokens are non-secret), so
// we treat a bare `token` field as a credential too — the install flow uses
// `state`, never `token`, to carry the one-time handle.
const BARE_TOKEN_RE = /^tokens?$/i;

function looksLikeCredentialKey(key) {
  return CREDENTIAL_KEY_RE.test(key) || BARE_TOKEN_RE.test(key);
}

/**
 * Deep-clone `value` while dropping any credential-shaped key. Returns a NEW
 * structure — never mutates the input. Used by the service response
 * interceptor and the store before anything is committed to reactive state.
 * `credential_present` (a boolean flag, not a secret) is explicitly allowed.
 */
export function stripCredentials(value) {
  if (Array.isArray(value)) return value.map((v) => stripCredentials(v));
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (k === "credential_present") {
        out[k] = v;
        continue;
      }
      if (looksLikeCredentialKey(k)) continue; // drop secret-shaped field
      out[k] = stripCredentials(v);
    }
    return out;
  }
  return value;
}

/**
 * Assert (returns boolean) that a payload carries no credential-shaped key at
 * any depth. Tests use this to prove nothing sensitive is about to be stored.
 */
export function containsCredential(value) {
  if (Array.isArray(value)) return value.some((v) => containsCredential(v));
  if (value && typeof value === "object") {
    return Object.entries(value).some(
      ([k, v]) => (k !== "credential_present" && looksLikeCredentialKey(k)) || containsCredential(v)
    );
  }
  return false;
}

// ---------------------------------------------------------------------------
// UUID vs provider-GID isolation (invariant 2)
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(v) {
  return typeof v === "string" && UUID_RE.test(v);
}

/** A Shopify GID looks like "gid://shopify/Location/123". Never a UUID. */
export function isProviderGid(v) {
  return typeof v === "string" && /^gid:\/\//i.test(v);
}

/**
 * Guard used before assigning any value to a UUID field (ravo_site_id,
 * catalog_store_uuid, ...). Throws if a provider GID / non-UUID is passed.
 * Returns the value on success so it can be used inline.
 */
export function assertUuidField(fieldName, value) {
  if (value == null) return value; // nullable is fine (pending / ignored)
  if (isProviderGid(value)) {
    throw new Error(`Refusing to write provider GID into UUID field "${fieldName}"`);
  }
  if (!isUuid(value)) {
    throw new Error(`Value for UUID field "${fieldName}" is not a UUID`);
  }
  return value;
}

// ---------------------------------------------------------------------------
// Lifecycle state machine (dashboard "state" — Production-Connection-Contract)
// ---------------------------------------------------------------------------

export const CONNECTION_STATES = Object.freeze({
  NOT_CONNECTED: "not_connected",
  AUTHORIZING: "authorizing",
  CONNECTED_MAPPING_REQUIRED: "connected_mapping_required",
  SYNCING: "syncing",
  READY: "ready",
  PARTIALLY_READY: "partially_ready",
  FAILED: "failed",
  REAUTHORIZATION_REQUIRED: "reauthorization_required",
  DISCONNECTED: "disconnected",
});

// Which operate-commands are valid for a given durable state. The UI reads
// this so a button is disabled (never merely hidden) when unavailable.
const STATE_ACTIONS = {
  not_connected: ["connect"],
  authorizing: [],
  connected_mapping_required: ["discover", "map", "sync", "disconnect", "reauthorize"],
  syncing: ["pause", "disconnect"],
  ready: ["discover", "sync", "reconcile", "pause", "disconnect", "reauthorize"],
  partially_ready: ["discover", "sync", "reconcile", "retry", "pause", "disconnect", "reauthorize"],
  failed: ["discover", "retry", "sync", "reconcile", "disconnect", "reauthorize"],
  reauthorization_required: ["reauthorize", "disconnect"],
  disconnected: ["reauthorize"],
};

export function actionsForState(state) {
  return STATE_ACTIONS[state] ? [...STATE_ACTIONS[state]] : [];
}

export function isActionAllowed(state, action) {
  return actionsForState(state).includes(action);
}

// ---------------------------------------------------------------------------
// Sync-run phase machine (UX §5 / Gate-2b §6)
// ---------------------------------------------------------------------------

export const SYNC_PHASES = Object.freeze([
  "authorised",
  "queued",
  "exporting",
  "transforming",
  "importing",
  "indexing",
  "ready",
  "failed",
]);

export const TERMINAL_PHASES = Object.freeze(["ready", "failed", "stale"]);

const CATALOGUE_PHASES = Object.freeze(SYNC_PHASES.filter((phase) => phase !== "failed"));

const RUN_PHASE_TRACKS = Object.freeze({
  catalogue: Object.freeze([
    { phase: "authorised", label: "Authorised" },
    { phase: "queued", label: "Queued" },
    { phase: "exporting", label: "Exporting" },
    { phase: "transforming", label: "Transforming" },
    { phase: "importing", label: "Importing" },
    { phase: "indexing", label: "Indexing" },
    { phase: "ready", label: "Ready" },
  ]),
  discovery: Object.freeze([
    { phase: "authorised", label: "Authorised" },
    { phase: "discovering", label: "Discovering" },
    { phase: "ready", label: "Ready" },
  ]),
  delta: Object.freeze([
    { phase: "authorised", label: "Authorised" },
    { phase: "applying_changes", label: "Applying changes" },
    { phase: "ready", label: "Ready" },
  ]),
});

/** The track is selected from the durable run type; progress remains run.phase-driven. */
export function phaseTrackForRunType(runType) {
  if (runType === "discovery") return RUN_PHASE_TRACKS.discovery;
  if (runType === "delta") return RUN_PHASE_TRACKS.delta;
  return RUN_PHASE_TRACKS.catalogue;
}

export function phaseIndex(phase) {
  return CATALOGUE_PHASES.indexOf(phase);
}

export function isTerminalPhase(phase) {
  return TERMINAL_PHASES.includes(phase);
}

/** 0..1 progress for the phase bar; failed/stale keep their last position. */
export function phaseProgress(phase) {
  if (phase === "ready") return 1;
  const i = phaseIndex(phase);
  if (i < 0) return 0;
  // authorised(0) .. indexing(5) span the active work; ready = done.
  return i / (CATALOGUE_PHASES.length - 1);
}

// ---------------------------------------------------------------------------
// Mapping lifecycle (Gate-2b §5)
// ---------------------------------------------------------------------------

export const MAPPING_STATUSES = Object.freeze({
  PENDING: "pending",
  MAPPED: "mapped",
  IGNORED: "ignored",
  DISABLED: "disabled",
});

export const IGNORE_REASONS = Object.freeze([
  "warehouse",
  "fulfilment_only",
  "inactive",
  "duplicate",
  "not_used",
]);

/**
 * Active provider locations that are still unmapped (neither linked nor
 * ignored). Returns the raw location records (empty => none pending).
 */
export function pendingActiveLocationRecords(locations) {
  if (!Array.isArray(locations)) return [];
  return locations
    .filter((loc) => loc && loc.provider_active !== false)
    .filter((loc) => {
      const status = loc.mapping?.status || MAPPING_STATUSES.PENDING;
      return status === MAPPING_STATUSES.PENDING;
    });
}

/**
 * Full sync is only allowed once every *active* provider location is resolved
 * (mapped or ignored). Returns the list of unresolved location display names
 * (empty => allowed). `mapped_only` + acknowledged omissions bypasses this.
 */
export function unresolvedActiveLocations(locations) {
  return pendingActiveLocationRecords(locations).map(
    (loc) => loc.display_name || loc.provider_location_id || "location"
  );
}

/**
 * Validate one mapping change before it is sent. Returns { ok, errors[] }.
 * `link` requires a UUID ravo_site_id; `create` requires a site_name;
 * `ignore` requires a valid ignore_reason. Guards GID-in-UUID.
 */
export function validateMappingChange(change) {
  const errors = [];
  if (!change || typeof change !== "object") {
    return { ok: false, errors: ["missing mapping change"] };
  }
  const { action } = change;
  if (action === "link") {
    if (!change.ravo_site_id) errors.push("ravo_site_id required to link a Site");
    else {
      try {
        assertUuidField("ravo_site_id", change.ravo_site_id);
      } catch (e) {
        errors.push(e.message);
      }
    }
  } else if (action === "create") {
    if (!String(change.site_name || "").trim()) {
      errors.push("site_name required");
    }
    if (change.ravo_site_id) {
      errors.push("create must not carry an existing ravo_site_id");
    }
  } else if (action === "ignore") {
    if (!IGNORE_REASONS.includes(change.ignore_reason)) {
      errors.push("valid ignore_reason required");
    }
  } else {
    errors.push(`unknown mapping action "${action}"`);
  }
  return { ok: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Misc presentational helpers
// ---------------------------------------------------------------------------

export function readableState(state) {
  const map = {
    not_connected: "Not connected",
    authorizing: "Authorizing",
    connected_mapping_required: "Mapping required",
    syncing: "Syncing",
    ready: "Ready",
    partially_ready: "Partially ready",
    failed: "Failed",
    reauthorization_required: "Reauthorization required",
    disconnected: "Disconnected",
  };
  return map[state] || state || "Unknown";
}

// A discovered provider location's `address` is a structured object passed
// through verbatim from Shopify's own Location.address shape (ai-back never
// formats it into a string) — join whatever fields are present into one
// readable line. Also tolerates a plain string for forward/backward safety.
export function formatProviderAddress(address) {
  if (!address) return "";
  if (typeof address === "string") return address;
  if (typeof address !== "object") return "";
  return [
    address.address1,
    address.address2,
    address.city,
    address.province,
    address.zip,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
}
